import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const LabeledInput = ({ label, value, onChange, testid }) => (
  <div className="flex items-center gap-2 border-b border-gray-200">
    <span className="w-14 text-xs text-gray-500">{label}</span>
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="flex-1 border-0 px-0 shadow-none focus-visible:ring-0"
    />
  </div>
);

/**
 * Compose email dialog. Reused by EmailTab (local flow) and the app-wide
 * ComposeProvider (triggered from other tabs like Employees).
 */
const ComposeDialog = ({ data, accounts, onClose, onSent }) => {
  const [form, setForm] = useState(data);
  const [files, setFiles] = useState(data.files || []);
  const [sending, setSending] = useState(false);

  const onFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    const read = await Promise.all(
      list.map(
        (f) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({
                filename: f.name,
                mime_type: f.type || "application/octet-stream",
                content_b64: String(reader.result).split(",")[1],
              });
            reader.readAsDataURL(f);
          }),
      ),
    );
    setFiles((prev) => [...prev, ...read]);
  };

  const send = async () => {
    if (!form.to.trim()) {
      toast.error("Add a recipient");
      return;
    }
    setSending(true);
    try {
      await api.post("/email/send", {
        account: form.account,
        to: form.to,
        cc: form.cc,
        subject: form.subject,
        body: (form.body || "").replace(/\n/g, "<br>"),
        thread_id: form.thread_id || null,
        attachments: files,
      });
      onSent?.();
    } catch {
      toast.error("Could not send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white text-gray-800 sm:max-w-2xl" data-testid="compose-dialog">
        <DialogHeader>
          <DialogTitle>
            {form.mode === "reply" ? "Reply" : form.mode === "forward" ? "Forward" : "New message"}
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Send an email from your connected account.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="w-14 text-xs text-gray-500">From</span>
            <select
              value={form.account}
              onChange={(e) => setForm((s) => ({ ...s, account: e.target.value }))}
              data-testid="compose-from"
              className="flex-1 rounded-md border border-gray-300 px-2 py-2 text-sm"
            >
              {accounts.map((a) => (
                <option key={a.email} value={a.email}>
                  {a.email}
                </option>
              ))}
            </select>
          </div>
          <LabeledInput label="To" value={form.to} onChange={(v) => setForm((s) => ({ ...s, to: v }))} testid="compose-to" />
          <LabeledInput label="Cc" value={form.cc} onChange={(v) => setForm((s) => ({ ...s, cc: v }))} testid="compose-cc" />
          <LabeledInput label="Subject" value={form.subject} onChange={(v) => setForm((s) => ({ ...s, subject: v }))} testid="compose-subject" />
          <textarea
            value={form.body}
            onChange={(e) => setForm((s) => ({ ...s, body: e.target.value }))}
            data-testid="compose-body"
            rows={10}
            className="w-full rounded-md border border-gray-300 p-3 text-sm focus:border-[#1a3fd1] focus:outline-none"
          />
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {files.map((f, i) => (
                <span key={i} className="flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-600">
                  <Paperclip className="h-3 w-3" /> {f.filename}
                  <button onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <label
            data-testid="compose-attach-btn"
            className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-md border border-gray-300 px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <Paperclip className="h-4 w-4" /> Attach
            <input type="file" multiple className="hidden" onChange={onFiles} data-testid="compose-attach-input" />
          </label>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="border-gray-300 text-gray-700">
              Discard
            </Button>
            <Button
              onClick={send}
              disabled={sending}
              data-testid="compose-send-btn"
              className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85"
            >
              {sending ? "Sending…" : "Send"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ComposeDialog;
